import {
  createApiToken as apiCreateApiToken,
  deleteApiToken as apiDeleteApiToken,
  updateApiToken as apiUpdateApiToken,
  getApiTokens,
} from "api/apiTokens";
import constate from "constate";
import { useFetchedState } from "hooks/useFetchedState";
import { useOpenGlobalErrorModal } from "hooks/useGlobalError";
import { useLoadingState } from "hooks/useLoadingState";
import { Map } from "immutable";
import { type ApiTokenModel } from "models/ApiTokenModel";
import { useCallback } from "react";

const useApiTokens = () => {
  const { data: apiTokens, loadData: loadApiTokens, setData: setApiTokens } = useFetchedState(getApiTokens);
  const { isLoading, withLoader } = useLoadingState();

  const openGlobalErrorModal = useOpenGlobalErrorModal();

  const getMap = useCallback(async (): Promise<Map<string, ApiTokenModel>> => {
    if (apiTokens) return apiTokens;
    try {
      return await withLoader(getApiTokens());
    } catch (error) {
      openGlobalErrorModal(error as Error);
      return Map();
    }
  }, [apiTokens, openGlobalErrorModal, withLoader]);

  const createApiToken = useCallback(
    async (name: string, duration: null | number) => {
      try {
        const createdToken = await apiCreateApiToken({ duration, name });
        setApiTokens((await getMap()).set(createdToken.id, createdToken));
        return createdToken;
      } catch (error) {
        openGlobalErrorModal(error as Error);
        return;
      }
    },
    [getMap, openGlobalErrorModal, setApiTokens],
  );

  const updateApiToken = useCallback(
    async (tokenId: string, name: string) => {
      try {
        const token = await apiUpdateApiToken(tokenId, name);
        const updatedToken = (await getMap()).get(token.id)?.set("name", token.name);

        if (!updatedToken) return;
        setApiTokens((await getMap()).set(token.id, updatedToken));

        return updatedToken;
      } catch (error) {
        openGlobalErrorModal(error as Error);
        return;
      }
    },
    [getMap, openGlobalErrorModal, setApiTokens],
  );

  const deleteApiToken = useCallback(
    async (tokenId: string) => {
      try {
        await apiDeleteApiToken(tokenId);

        const tokens = await getMap();
        const revokedToken = tokens.get(tokenId)?.set("deletedAt", new Date());

        if (!revokedToken) return;
        setApiTokens(tokens.set(tokenId, revokedToken));
      } catch (error) {
        openGlobalErrorModal(error as Error);
        return;
      }
    },
    [getMap, openGlobalErrorModal, setApiTokens],
  );

  return {
    actions: { createApiToken, deleteApiToken, loadApiTokens, updateApiToken },
    state: { apiTokens, isLoading },
  };
};

export const [ApiTokensProvider, useApiTokensContext] = constate(useApiTokens);
